import { ENROLLMENT_STEP_LABEL, PAYMENT_METHODS } from '@app/shared';
import type {
  EnrollmentApplicant,
  EnrollmentDto,
  EnrollmentSource,
  PaymentMethodKey,
} from '@app/shared';
import { APP_NAME } from '../../app/theme';

/** The wizard pages of one enrollment: steps 2 to 4 and the confirmation. */
export type WizardStep = 2 | 3 | 4 | 'done';

export const stepPath = (id: string, step: WizardStep): string =>
  step === 'done' ? `/enrollments/${id}/done` : `/enrollments/${id}/step/${step}`;

/** The page an enrollment is waiting on: its next step, or the confirmation once complete. */
export const currentStep = (e: Pick<EnrollmentDto, 'status' | 'step'>): WizardStep =>
  e.status === 'Complete' ? 'done' : e.step === 3 || e.step === 4 ? e.step : 2;

/** "Enrollment step" column: the number and what the step asks for. */
export const stepLabel = (step: number | null): string =>
  step === null ? '' : `${step} - ${ENROLLMENT_STEP_LABEL[step] ?? ''}`;

/** The reference shows its own product name for orders taken in the application itself. */
export const sourceLabel = (source: EnrollmentSource): string =>
  source === 'BSS' ? APP_NAME : source;

export const paymentLabel = (method: PaymentMethodKey): string =>
  PAYMENT_METHODS.find((m) => m.key === method)?.label ?? method;

export const customerName = (a: EnrollmentApplicant | null): string =>
  a ? [a.firstName, a.middleInitial, a.lastName].filter(Boolean).join(' ') : '';

/** Service address on one line. */
export const serviceAddress = (a: EnrollmentApplicant | null): string =>
  a
    ? `${[a.addressLine1, a.addressLine2, a.city].filter(Boolean).join(', ')}, ${a.state} ${a.zip}`
    : '';

export const mailingAddress = (a: EnrollmentApplicant | null): string => {
  if (!a) return '';
  if (a.mailingType === 'same' || !a.mailing) return serviceAddress(a);
  const m = a.mailing;
  return `${[m.line1, m.line2, m.city].filter(Boolean).join(', ')}, ${m.state} ${m.zip}`;
};
